import React from "react";
import { useDispatch } from "react-redux";
import { useRouteError, isRouteErrorResponse } from "react-router-dom";
import { Collapse } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
	IconChevronDown,
	IconChevronUp,
	IconHome,
	IconLogout,
	IconProgressHelp,
} from "@tabler/icons-react";

import { ButtonWithIcon } from "../ButtonWithIcon";
import { ErrorHeader } from "../HeaderBar";
import LexWorkplaceLogo from "../../svg/LexWorkplaceLogo";
import { LexWorkplaceRefreshIcon } from "../LexWorkplaceIcons";
import { logOut } from "../../actions/userAuthActions";
import "./FullPageError.css";
import "./ErrorBoundaryPage.css";

const getErrorDetails = (error) => {
	if (isRouteErrorResponse(error)) {
		return {
			message: `${error.status} ${error.statusText}`,
			stack: error.data ? JSON.stringify(error.data, null, 2) : null,
		};
	}
	if (error instanceof Error) {
		return { message: error.message, stack: error.stack };
	}
	return { message: String(error), stack: null };
};

const ErrorBoundaryPage = () => {
	const error = useRouteError();
	const dispatch = useDispatch();
	const { message, stack } = getErrorDetails(error);
	const [detailsOpened, { toggle: toggleDetails }] = useDisclosure(false);

	const handleReload = () => {
		window.location.reload();
	};

	const handleGoHome = () => {
		window.location.href = "/";
	};

	const handleLogOut = () => {
		dispatch(logOut());
	};

	return (
		<div className="App">
			<ErrorHeader />
			<div className="full-page-error">
				<div>
					<div className="error-content">
						<div className="error-logo">
							<LexWorkplaceLogo />
						</div>
						<div className="error-text-block">
							<div className="error-title">
								SOMETHING WENT WRONG
							</div>
							<div className="error-text">
								LexWorkplace ran into an unexpected error
							</div>
							<div className="error-subtext">
								Please reload the page. If the problem
								continues, contact Uptime Legal support.
							</div>
						</div>
					</div>
					<div className="error-buttons">
						<ButtonWithIcon
							handler={handleReload}
							buttonText="Reload Page"
							iconComponent={<LexWorkplaceRefreshIcon />}
						/>
						<ButtonWithIcon
							handler={handleGoHome}
							buttonText="Back to Homepage"
							iconComponent={
								<IconHome
									size={20}
									style={{ verticalAlign: "middle" }}
								/>
							}
						/>
						<a
							href="https://help.lexworkplace.com/"
							target="_blank"
							rel="noopener noreferrer"
							className="error-button-link"
						>
							<ButtonWithIcon
								buttonText="Contact Support"
								iconComponent={
									<IconProgressHelp
										size={20}
										style={{ verticalAlign: "middle" }}
									/>
								}
							/>
						</a>
						<ButtonWithIcon
							handler={handleLogOut}
							buttonText="Log Out"
							iconComponent={
								<IconLogout
									size={20}
									style={{ verticalAlign: "middle" }}
								/>
							}
						/>
					</div>
					<button
						type="button"
						className="error-details-toggle"
						onClick={toggleDetails}
					>
						Technical details
						{detailsOpened ? (
							<IconChevronUp size={16} />
						) : (
							<IconChevronDown size={16} />
						)}
					</button>
					<Collapse in={detailsOpened}>
						<pre className="error-details-stack">
							{message}
							{stack ? `\n\n${stack}` : null}
						</pre>
					</Collapse>
				</div>
			</div>
		</div>
	);
};

export default ErrorBoundaryPage;
